import { CancellationTokenSource } from "../Promises/CancellationTokenSource";
import { IRightsContext } from "./IRightsContext";
import { IRightsEvaluationFunction, IRightsEvaluationScheduler } from "./IRightsEvaluationScheduler";
import { RightType } from "./RightTypes";

/** Informace o probíhajícím vyhodnocení práv */
interface IEvaluationInfo {
    /** Požadovaná práva */
    runningRights: RightType[];
    /** Požadovaná práva, která mají být vyhodnocena přednostně */
    priorityRights: RightType[] | undefined;
    /** Úloha vyhodnocení práv */
    task: Promise<void> | undefined;
    /** CTS pro zrušení operace */
    cancellationTokenSource: CancellationTokenSource;
}

/**
 * Ihned spustí vyhodnocení práv, nezávisle na právě probíhajících
 * @internal
 */
export class ParallelRightsEvaluationScheduler implements IRightsEvaluationScheduler {
    private _disposed = false;

    private _runningRightsMap: Map<string, IEvaluationInfo[]> = new Map();

    public schedule(rightTypes: RightType[], context: IRightsContext | null, evaluator: IRightsEvaluationFunction, priorityRightTypes: RightType[] = [], senderUuid: string): Promise<void> {
        if (this._disposed) {
            throw new Error("Cannot schedule evaluation on disposed scheduler.");
        }

        const taskId: string = context?.id || senderUuid;
        const cts = new CancellationTokenSource();
        const evaluationDefinition: IEvaluationInfo = {
            runningRights: rightTypes,
            priorityRights: priorityRightTypes,
            task: undefined,
            cancellationTokenSource: cts,
        };

        const evaluation = evaluator(rightTypes.map(r => ({ type: r, ct: cts.token })), context, cts.token, priorityRightTypes?.map(r => ({ type: r, ct: cts.token })))
            .then(() => this._removeTask(taskId, evaluationDefinition))
            .catch(e => {
                if (!this._disposed) { // pokud je scheduler již zrušený, je zrušené i pole běžících vyhodnocení a proto není z čeho odebírat
                    this._removeTask(taskId, evaluationDefinition);
                }
                return e;
            });

        evaluationDefinition.task = evaluation;

        return this._addEvaluationTask(taskId, evaluationDefinition);
    }

    private _addEvaluationTask(taskId: string, evaluationDefinition: IEvaluationInfo): Promise<void> {
        if (!this._runningRightsMap.has(taskId)) {
            this._runningRightsMap.set(taskId, []);
        }
        const runningEvaluations = this._runningRightsMap.get(taskId);
        runningEvaluations.push(evaluationDefinition);
        //Záměrně tu vracím všechny promisy pro taskID. Ošetří to situaci, kdy scheduler zjistí že část práv se vyhodnocuje v existujícím promisu a část musí naplánovat.
        //Kdyby se vrátil promise jen na nově plánovéné vyhodnocení, mohl by teoreticky doběhnout dříve než ten už běžící a rights manager by neměl jistotu, že byly vyhodoceny
        //všechny práva, která požadoval.
        return Promise.all(runningEvaluations.map(i => i.task)).then();
    }

    private _removeTask(taskId: string | undefined, evaluationDefinition: IEvaluationInfo): void {
        const runningEvaluations = this._runningRightsMap.get(taskId);
        const remainingEvaluations = runningEvaluations.filter(re => re !== evaluationDefinition);
        if (remainingEvaluations.length === 0) {
            this._runningRightsMap.delete(taskId);
        } else {
            this._runningRightsMap.set(taskId, remainingEvaluations);
        }
    }

    public dispose(): void {
        if (this._disposed) {
            return;
        }

        this._disposed = true;
        this._runningRightsMap.forEach(item =>
            item.forEach(runningEvaluation =>
                runningEvaluation.cancellationTokenSource.cancel()),
        );
    }
}
